import { Button, InputV2, Label, Text } from '@southfields-digital/mxmz-react-ui';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { Dialog } from 'src/components/Dialog';
import { Contestant } from 'src/types/matchControl/contestant';
import { Member } from 'src/types/matchControl/member';

import {
  LINE_UP_PRESETS,
  LINE_UP_PRESET_OPTIONS,
  LineUpPreset,
  isLineUpPreset,
} from './memberColumns';
import MembersList from './MembersList';
import PasteMembers from './PasteMembers';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  contestant: Contestant;
  preset: LineUpPreset;
  onSubmit: (members: Contestant['members'], preset: LineUpPreset) => void;
};

const LineUpDialog = ({ open, setOpen, contestant, preset, onSubmit }: Props) => {
  const [members, setMembers] = useState(contestant.members || []);
  // Picking a preset only takes effect on submit, so cancelling keeps the rows.
  const [selectedPreset, setSelectedPreset] = useState(preset);

  useEffect(() => {
    // Only follow the store while closed. The match control is pushed over a
    // socket, so syncing on every update would overwrite rows being edited — and
    // after a preset switch it would bring them back under the wrong columns.
    if (open) return;

    setMembers(contestant.members || []);
  }, [open, contestant.members]);

  useEffect(() => {
    if (open) return;

    setSelectedPreset(preset);
  }, [open, preset]);

  const handleClickCancel = () => {
    setOpen(false);
    setMembers(contestant.members || []);
    setSelectedPreset(preset);
  };

  const handleImportMembers = (importedMembers: Member[]) => {
    setMembers((prev) => [...prev, ...importedMembers]);
  };

  const handleSelectPreset = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (!isLineUpPreset(selected) || selected === selectedPreset) return;

    setSelectedPreset(selected);
    // The stored rows belong to the stored preset's columns, so they only survive
    // a switch back to it.
    setMembers(selected === preset ? contestant.members || [] : []);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className="w-[960px] max-w-[960px]">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-4">
          <Text as="h2">Line-up: {contestant.name}</Text>
          <div className="flex items-center gap-2">
            <Label htmlFor="lineUpPreset" className="!m-0 whitespace-nowrap !text-white">
              Columns
            </Label>
            <InputV2
              id="lineUpPreset"
              type="select"
              inputSize="lg"
              value={selectedPreset}
              options={LINE_UP_PRESET_OPTIONS}
              onChange={handleSelectPreset}
              wrapperClassName={classNames(
                '!w-[200px]',
                // TODO: REMOVE THIS STYLING OVERRIDE WHEN ALL INPUTS USE INPUTV2
                '!min-h-[42px] !rounded-[4px] !bg-elevated-0 !border-gray-800'
              )}
            />
          </div>
        </div>

        <PasteMembers
          key={`${open}-${selectedPreset}`}
          preset={selectedPreset}
          onImport={handleImportMembers}
        />
        <MembersList
          members={members}
          columns={LINE_UP_PRESETS[selectedPreset].columns}
          setMembers={setMembers}
        />
        <div className="flex justify-between gap-2">
          <Button onClick={handleClickCancel} variant="danger">
            Cancel
          </Button>
          <Button onClick={() => onSubmit(members, selectedPreset)} color="primaryLight">
            Submit
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default LineUpDialog;
